import { MapLayerConfig, RasterLayerConfig } from "../types";

export type WmsRasterLayerConfig = RasterLayerConfig & {
  queryParams: NonNullable<RasterLayerConfig["queryParams"]>;
};

/**
 * A raster layer is treated as a WMS layer if its query params advertise service=WMS.
 * This covers every WMS endpoint we currently use (GeoServer, ArcGIS MapServer/WMSServer)
 * without needing extra config fields.
 */
export const isWmsRasterLayer = (layer: MapLayerConfig): layer is WmsRasterLayerConfig => {
  if (layer.type !== "raster") return false;
  const service = layer.queryParams?.SERVICE ?? layer.queryParams?.service;
  return service?.toUpperCase() === "WMS";
};

/**
 * The store mutates `layer.url` via {@link processLayerUrls} to include the merged
 * query string, so we need the bare base to build a fresh GetLegendGraphic URL.
 */
const getBaseUrl = (url: string): string => url.split("?")[0];

/** Canonical version lookup honouring whichever case the config used. */
const getVersion = (queryParams: Record<string, string>): string => queryParams.VERSION ?? queryParams.version ?? "1.3.0";

/**
 * Build one GetLegendGraphic URL per sub-layer named in the config's `LAYERS` param.
 * WMS GetLegendGraphic accepts a single `LAYER` (singular) so multi-layer configs
 * yield multiple legend images that the UI can stack.
 *
 * URL matches the canonical form advertised in GeoServer's `<LegendURL>` entries
 * — lowercase keys, url-encoded `format`, fixed parameter order — so browsers and
 * servers see the same cache key regardless of how the source config was spelled.
 *
 * Returns an empty array when the layer isn't a WMS layer or has no `LAYERS` param.
 */
export const buildWmsLegendUrls = (layer: MapLayerConfig): string[] => {
  if (!isWmsRasterLayer(layer)) return [];

  // Honour a per-layer explicit override before doing any GetLegendGraphic composition.
  // This lets customers pin a server-advertised `<LegendURL>` verbatim (e.g. a 38x595
  // raster ramp) when the default 20x20 swatch isn't appropriate for that sub-layer.
  if (layer.legendUrl) return [layer.legendUrl];

  const layersParam = layer.queryParams.LAYERS ?? layer.queryParams.layers;
  if (!layersParam) return [];

  const sublayerNames = layersParam
    .split(",")
    .map((name) => name.trim())
    .filter(Boolean);
  if (sublayerNames.length === 0) return [];

  const base = getBaseUrl(layer.url);
  const version = getVersion(layer.queryParams);

  return sublayerNames.map((sublayer) => {
    // Emit params in the canonical order GeoServer publishes in GetCapabilities
    // (service, version, request, format, width, height, layer). URLSearchParams
    // preserves insertion order, so appending one-by-one guarantees the output.
    const params = new URLSearchParams();
    params.append("service", "WMS");
    params.append("version", version);
    params.append("request", "GetLegendGraphic");
    params.append("format", "image/png");
    params.append("width", "20");
    params.append("height", "20");
    params.append("layer", sublayer);
    return `${base}?${params.toString()}`;
  });
};
